import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'

import DraftsIcon from '@mui/icons-material/Drafts'
import SendIcon from '@mui/icons-material/Send'
import EditIcon from '@mui/icons-material/Edit'

import { responsePageReadOnlyStatuses } from '../../util/foiConsts'
import { FlexRow, FlexColumn, FlexEndContainer, OutlineContainer } from '../styles/Containers'
import { MenuButton, SecondaryButton } from '../common/Button'
import CreateDraftCommunicationFromTemplateButton from './CreateDraftCommunicationFromTemplateButton'
import DeleteCommunicationButton from './DeleteCommunicationButton'
import CommunicationTable from './CommunicationTable'
import CommunicationEditable from './CommunicationEditable'
import DownloadCommunicationButton from './DownloadCommunicationButton'
import CommunicationReadOnly from './CommunicationReadOnly'
import SendCommunicationButton from './SendCommunicationButton'

const ContentColumn = styled(FlexColumn)`
  flex: 1;
  min-width: 0;
`

function CommunicationsTab({
  foiId,
  foiStatus,
  tab = 'DRAFT',
  isEditing = false,
  hasEmail,
  activeCommunication,
  communications = [],
  templates = [],
  letters = [],
  bills = [],
  publicConfig = {},
  handleUpdateIsEditing = () => {},
  handleUpdateActiveTab = () => {},
  handleCreateDraft = () => {},
  handleDeleteDraft = () => {},
  handleEditDraft = () => {},
  handleSendCommunication = () => {},
  handleUpdateCommunication = () => {},
  handleViewCommunication = () => {}
}) {
  const [activeTab, setActiveTab] = useState()
  const [currentIsEditing, setCurrentIsEditing] = useState(isEditing)
  const [currentActiveCommunication, setCurrentActiveCommunication] = useState(activeCommunication)

  function stopEditing() {
    setCurrentIsEditing(false)
    handleUpdateIsEditing(false)
  }

  function handleClickTab(newTab) {
    setActiveTab(newTab)
    handleUpdateActiveTab(newTab)
    setCurrentIsEditing(false)
    handleUpdateIsEditing(false)
    setCurrentActiveCommunication()
  }

  useEffect(() => {
    setActiveTab(tab)
  }, [tab])

  useEffect(() => {
    setCurrentIsEditing(isEditing)
  }, [isEditing])

  useEffect(() => {
    setCurrentActiveCommunication(activeCommunication)
  }, [activeCommunication])

  return (
    <FlexRow>
      <FlexColumn gap="5px">
        <MenuButton active={(activeTab === 'DRAFT').toString()} onClick={() => handleClickTab('DRAFT')}>
          <DraftsIcon />
          Drafts
        </MenuButton>
        <MenuButton active={(activeTab === 'SENT').toString()} onClick={() => handleClickTab('SENT')}>
          <SendIcon />
          Sent
        </MenuButton>
      </FlexColumn>
      {activeTab && (
        <ContentColumn gap="10px">
          {!currentIsEditing && (
            <>
              {!currentActiveCommunication && activeTab === 'DRAFT' && !responsePageReadOnlyStatuses.includes(foiStatus) && (
                <FlexEndContainer>
                  <CreateDraftCommunicationFromTemplateButton
                    foiId={foiId}
                    communicationTemplates={templates}
                    handleCreateDraft={handleCreateDraft}
                  />
                </FlexEndContainer>
              )}
              {currentActiveCommunication && activeTab === 'DRAFT' && (
                <FlexEndContainer gap="5px">
                  <DeleteCommunicationButton
                    state="DRAFT"
                    id={currentActiveCommunication.id}
                    name={currentActiveCommunication.name}
                    onSuccess={handleDeleteDraft}
                  />
                  {!responsePageReadOnlyStatuses.includes(foiStatus) && (
                    <>
                      <SecondaryButton onClick={() => handleEditDraft(currentActiveCommunication.id)}>
                        <EditIcon /> Edit
                      </SecondaryButton>
                      {hasEmail !== undefined && (
                        <SendCommunicationButton
                          id={currentActiveCommunication.id}
                          name={currentActiveCommunication.name}
                          hasEmail={hasEmail}
                          onSuccess={handleSendCommunication}
                        />
                      )}
                    </>
                  )}
                </FlexEndContainer>
              )}
              {currentActiveCommunication && activeTab === 'SENT' && (
                <DownloadCommunicationButton id={currentActiveCommunication.id} name={currentActiveCommunication.name} />
              )}
              {!currentActiveCommunication ? (
                <OutlineContainer padding="15px 10px">
                  {activeTab === 'DRAFT' && (
                    <CommunicationTable
                      state="DRAFT"
                      disableEditing={responsePageReadOnlyStatuses.includes(foiStatus)}
                      communications={communications.filter(c => c.state === 'DRAFT')}
                      onEditCommunication={handleEditDraft}
                      handleSelectCommunication={handleViewCommunication}
                    />
                  )}
                  {activeTab === 'SENT' && (
                    <CommunicationTable
                      state="SENT"
                      communications={communications.filter(c => c.state === 'SENT')}
                      handleSelectCommunication={handleViewCommunication}
                    />
                  )}
                </OutlineContainer>
              ) : (
                <OutlineContainer padding="25px 30px">
                  <CommunicationReadOnly state={activeTab} communication={currentActiveCommunication} publicConfig={publicConfig} />
                </OutlineContainer>
              )}
            </>
          )}
          {currentIsEditing && currentActiveCommunication && activeTab === 'DRAFT' && (
            <CommunicationEditable
              communication={currentActiveCommunication}
              publicConfig={publicConfig}
              letters={letters}
              bills={bills}
              handleUpdateCommunication={handleUpdateCommunication}
              handleCancelUpdateCommunication={stopEditing}
            />
          )}
        </ContentColumn>
      )}
    </FlexRow>
  )
}

CommunicationsTab.propTypes = {
  foiId: PropTypes.string.isRequired,
  foiStatus: PropTypes.string.isRequired,
  tab: PropTypes.oneOf(['DRAFT', 'SENT']),
  isEditing: PropTypes.bool,
  hasEmail: PropTypes.bool,
  activeCommunication: PropTypes.object,
  communications: PropTypes.array,
  templates: PropTypes.array,
  letters: PropTypes.array,
  bills: PropTypes.array,
  publicConfig: PropTypes.object,
  handleUpdateIsEditing: PropTypes.func,
  handleUpdateActiveTab: PropTypes.func,
  handleCreateDraft: PropTypes.func,
  handleDeleteDraft: PropTypes.func,
  handleEditDraft: PropTypes.func,
  handleSendCommunication: PropTypes.func,
  handleUpdateCommunication: PropTypes.func,
  handleViewCommunication: PropTypes.func
}

export default CommunicationsTab
